import React from "react";
import { StyleSheet, TouchableOpacity, View } from "react-native";
import { useSelector } from "react-redux";
import { useIsMobile } from "src/hooks/ui";

import { kColors } from "src/kColors";
import { kStyleConst } from "src/kStyle";
import { useAppDispatch } from "src/redux";
import { acceptTerms } from "src/redux/company/actions";
import { openUrlInNewTab } from "src/util/linking";

import { PdButton, PdButtonLabel } from "../forms/button";
import { PdBell } from "../Icons";
import { PdText } from "../layout";
import { PdModalCloseButton } from "../overlay";

const AcknowledgementBox: React.FC = () => {
  const isMobile = useIsMobile();
  const dispatch = useAppDispatch();
  const rTermsAndConditionsUrl = useSelector((state) => state.ConfigsReducer.data.urls?.termsAndConditions);
  const rIsLoading = useSelector((state) => state.LoadingReducer.companyFetching);
  const rIsTermsAccepted = useSelector(
    (state) =>
      (state.CompanyReducer.type === "brand" || state.CompanyReducer.type === "retailer") &&
      !!state.CompanyReducer.isTermsAndConditionsAccepted,
  );
  const [show, setShow] = React.useState(false);

  const handleOnPressTerms = () => {
    if (rTermsAndConditionsUrl) openUrlInNewTab(rTermsAndConditionsUrl);
  };

  const handleOnPressAcknowledge = async () => {
    await dispatch(acceptTerms());
    setShow(false);
  };

  React.useEffect(() => {
    if (!rIsLoading) {
      setShow(!rIsTermsAccepted);
    }
  }, [rIsLoading, rIsTermsAccepted]);

  if (rIsLoading || !show) {
    return (
      <View style={styles.zIndex1}>
        <TouchableOpacity style={isMobile ? styles.mobileButton : styles.button} onPress={() => setShow(true)}>
          <PdBell color={kColors.white} style={styles.heightMd} />
        </TouchableOpacity>
      </View>
    );
  }

  return (
    <View style={styles.zIndex1}>
      <View style={[isMobile ? styles.mobileBox : styles.box, rIsTermsAccepted ? styles.pr14 : {}]}>
        {rIsTermsAccepted && <PdModalCloseButton colorScheme="white" onPress={() => setShow(false)} />}
        <PdText
          variant="body-sm"
          style={[styles.colorWhite, isMobile ? [styles.leading2, styles.textSm] : styles.leading3]}
        >
          Suggestions and invitations to place orders offline or outside of The Main Tab platform is in violation of our{" "}
          <PdText
            variant="body-sm"
            onPress={handleOnPressTerms}
            style={[
              styles.textUnderline,
              styles.colorWhite,
              isMobile ? [styles.leading2, styles.textSm] : styles.leading3,
            ]}
          >
            Terms & Conditions
          </PdText>
          . While we understand that sometimes special circumstances may be present, please note that evidence of
          platform circumvention may result in account suspension or permanent deactivation.
        </PdText>
        {!rIsTermsAccepted && (
          <PdButton
            variant="outline"
            colorScheme="white"
            style={[styles.mt4, styles.mlAuto]}
            onPress={handleOnPressAcknowledge}
          >
            <PdButtonLabel>Acknowledge</PdButtonLabel>
          </PdButton>
        )}
      </View>
    </View>
  );
};

export default AcknowledgementBox;

const styles = StyleSheet.create({
  box: {
    backgroundColor: kColors.brandPurple,
    borderRadius: kStyleConst.kSpacing * 3,
    left: kStyleConst.kSpacing * 8,
    paddingHorizontal: kStyleConst.kSpacing * 8,
    paddingVertical: kStyleConst.kSpacing * 4,
    position: "absolute",
    right: kStyleConst.kSpacing * 8,
    top: kStyleConst.kSpacing * 4,
  },
  button: {
    backgroundColor: kColors.brandPurple,
    borderRadius: 999,
    padding: kStyleConst.kSpacing * 2,
    position: "absolute",
    right: kStyleConst.kSpacing * 8,
    top: 15,
  },
  colorWhite: {
    color: kColors.white,
  },
  heightMd: {
    height: 18,
  },
  leading2: {
    lineHeight: 22,
  },
  leading3: {
    lineHeight: 25,
  },
  mlAuto: {
    marginLeft: "auto",
  },
  mobileBox: {
    backgroundColor: kColors.brandPurple,
    borderRadius: kStyleConst.kSpacing * 3,
    left: kStyleConst.kSpacing * 4,
    paddingHorizontal: kStyleConst.kSpacing * 6,
    paddingVertical: kStyleConst.kSpacing * 4,
    position: "absolute",
    right: kStyleConst.kSpacing * 4,
    top: kStyleConst.kSpacing * 3,
  },
  mobileButton: {
    backgroundColor: kColors.brandPurple,
    borderRadius: 999,
    padding: kStyleConst.kSpacing * 2,
    position: "absolute",
    right: kStyleConst.kSpacing * 4,
    top: 15,
  },
  mt4: {
    marginTop: 4 * kStyleConst.kSpacing,
  },
  pr14: {
    paddingRight: 14 * kStyleConst.kSpacing,
  },
  textSm: {
    fontSize: 12,
  },
  textUnderline: {
    textDecorationLine: "underline",
  },
  zIndex1: {
    zIndex: 1,
  },
});
